import { IconButton, LoadingButton, Popconfirm, Stack } from "@altimateai/lego";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FormItem,
  Input,
  Textarea,
} from "@altimateai/lego";
import { ProjectGovernorCheck, ProjectGovernorCheckSchema } from "./types";
import { EditIcon, DeleteIcon } from "../../../assets/icons";
import { useMutation, ApiHelper } from "../../api/index";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, toast } from "@altimateai/lego";
import { FormMessage as FormFeedback } from "@altimateai/lego";

interface Props {
  check: ProjectGovernorCheck;
  refetch: () => void;
}
const ProjectGovernanceCheck = ({ check, refetch }: Props) => {
  const [isInEditMode, setIsInEditMode] = useState(false);
  const isSelected = new URLSearchParams(window.location.search).get("id") === `${check.id}`;

  // Create a type that omits the id field for form validation
  type ProjectGovernorCheckFormData = Omit<ProjectGovernorCheck, "id">;

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
    reset,
  } = useForm<ProjectGovernorCheckFormData>({
    defaultValues: check,
    resolver: zodResolver(ProjectGovernorCheckSchema),
  });

  const onSubmit = async (values: ProjectGovernorCheckFormData) => {
    try {
      await ApiHelper.post(`project_governance/check/${check.id}`, values, {
        method: "PUT",
      });
      setIsInEditMode(false);
      refetch();
    } catch (error) {
      toast({
        title: "Failed to update project governance check",
        description: (error as Error).message || (error as string),
        variant: "destructive",
      });
    }
  };

  const { mutate, loading: isDeleting } = useMutation({
    queryFn: (checkName: string) =>
      ApiHelper.post(
        `project_governance/check/${check.id}`,
        { data: { check_name: checkName } },
        { method: "DELETE" }
      ),
    onSuccess: () => {
      refetch();
    },
  });

  const onEditClick = () => {
    setIsInEditMode(true);
  };

  const onEditCancel = () => {
    setIsInEditMode(false);
    reset(check);
  };

  return (
    <Card className={isSelected ? "al-border-primary" : ""} style={{ borderWidth: 1 }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CardHeader>
          <CardTitle>
            <Stack justify="between">
              {isInEditMode ? (
                <FormItem>
                  <Input
                    {...register("name")}
                    placeholder="Project governance check name..."
                    className={errors.name ? "al-border-destructive" : ""}
                  />
                  {errors.name ? <FormFeedback>{errors.name.message}</FormFeedback> : null}
                </FormItem>
              ) : (
                check.name
              )}

              <Stack align="center" gap="none">
                {isInEditMode ? (
                  <Stack>
                    <Button onClick={onEditCancel} variant="outline" size="sm">
                      Cancel
                    </Button>
                    <LoadingButton
                      size="sm"
                      loading={isSubmitting}
                      disabled={isSubmitting || !isValid}
                      type="submit"
                    >
                      Save
                    </LoadingButton>
                  </Stack>
                ) : (
                  <>
                    <IconButton variant="ghost" title="Edit this learning" onClick={onEditClick}>
                      <EditIcon />
                    </IconButton>

                    <Popconfirm
                      title="Delete the check"
                      description="Are you sure to delete this check?"
                      onConfirm={() => mutate(check.name)}
                      okText="Yes"
                      cancelText="No"
                    >
                      <LoadingButton
                        variant="ghost"
                        title="Delete check"
                        color="link"
                        loading={isDeleting}
                      >
                        <DeleteIcon />
                      </LoadingButton>
                    </Popconfirm>
                  </>
                )}
              </Stack>
            </Stack>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p>
            {isInEditMode ? (
              <FormItem>
                <Textarea
                  {...register("description")}
                  placeholder="Describe the project governance check..."
                  className={
                    errors.description
                      ? "al-border-destructive al-min-h-[100px]"
                      : "al-min-h-[100px]"
                  }
                />
                {errors.description ? (
                  <FormFeedback>{errors.description.message}</FormFeedback>
                ) : null}
              </FormItem>
            ) : (
              check.description
            )}
          </p>
          <p>Type: {check.type}</p>
          <p>Files required: {check.files_required.join(", ")}</p>
        </CardContent>
      </form>
    </Card>
  );
};

export default ProjectGovernanceCheck;
